/**
 * Copyright ToolBelt Data Inc., 2021 All Rights Reserved
 */

import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {
    FormGroup,
    FormControl,
} from 'react-bootstrap';
import FormRequiredText from "./form/FormRequiredText";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {APP_COLORS} from "../Constants";
import {faLock} from "@fortawesome/pro-solid-svg-icons";
import styled from "styled-components";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import FormLabel from "./form/FormLabel";

const DivLockRelative = styled.div`
    position: relative;
`;

const DivLockAbsolute = styled.div`
    position: absolute;

    right: 0;
    top: -24px;
`;

const ClearRelative = styled.div`
    position: relative;
    width: 100%;
`;

const ClearAbsolute = styled.div`
    position: absolute;

    right: 10px;
    top: 50%;
    transform: translateY(-50%);

    cursor: pointer;
    z-index: 10;
    pointer-events: auto;
    opacity: 0.5;
    transition: opacity 0.15s ease-in-out, color 0.15s ease-in-out;

    svg,
    svg.svg-inline--fa,
    .svg-inline--fa,
    svg[class*="svg-inline"] {
        font-size: 20px !important;
        width: 20px !important;
        height: 20px !important;
        min-width: 20px !important;
        min-height: 20px !important;
        max-width: 20px !important;
        max-height: 20px !important;
    }

    &:hover {
        opacity: 1;
        color: ${APP_COLORS['red']} !important;

        svg,
        svg.svg-inline--fa,
        .svg-inline--fa,
        svg[class*="svg-inline"] {
            font-size: 20px !important;
            width: 20px !important;
            height: 20px !important;
            min-width: 20px !important;
            min-height: 20px !important;
            max-width: 20px !important;
            max-height: 20px !important;
        }
    }
`;

const getRegExp = (type) => {
    let regx = null;
    switch (type) {
        case 'EMAIL':
            regx =
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            break;
        case 'URL':
            regx =
                /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/gm;
            break;
        case 'PHONE':
            regx =
                /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
            break;
        case 'TITLE':
            regx =
                /[A-Za-z]/ig;
            break;
        default:
            break;
    }
    return regx;
};
const validationHandler = (e, props) => {
    if (!props.onValidateFunc) return;
    const {value, name} = e.target;
    let msg = null;

    if (!value && props.isReq) {
        msg = `Please enter ${props.title}.`;
    } else if (value && props.reqType && !getRegExp(props.reqType).test(value)) {
        msg = `Please enter valid ${props.title}.`;
    } else if (props.min && value.length < props.min) {
        msg = `${props.title} must be at least ${props.min} characters long.`;
    } else if (props.max && value.length > props.max) {
        msg = `${props.title} is more than ${props.max} characters long.`;
    }

    props.onValidateFunc(msg, name);
};

const FormTextInput = (props) => {
    const {
        clearable,
        clearableHandle,
        isPreview = false,
        infoText = '',
        height,
    } = props;

    const formGroupRef = useRef(null);

    // Remove any rbt-close buttons that might be added dynamically
    useEffect(() => {
        const removeRbtCloseButtons = () => {
            if (formGroupRef.current) {
                // Look for rbt-close buttons anywhere in the form group
                const rbtCloseButtons = formGroupRef.current.querySelectorAll('button.rbt-close, button.btn-close.rbt-close, .rbt-close, .btn-close.rbt-close');
                rbtCloseButtons.forEach(button => {
                    // Only remove if not inside a .rbt container (Typeahead component)
                    if (!button.closest('.rbt') && !button.closest('.rbt-aux')) {
                        // Debug: log where this button came from
                        console.warn('Found rbt-close button in FormTextInput:', {
                            button,
                            parentElement: button.parentElement,
                            formControlId: props.id,
                            stackTrace: new Error().stack
                        });
                        button.style.display = 'none';
                        button.style.visibility = 'hidden';
                        button.remove();
                    }
                });
            }
        };

        // Remove immediately
        removeRbtCloseButtons();

        // Also check periodically in case buttons are added after mutations
        const intervalId = setInterval(removeRbtCloseButtons, 100);

        if (formGroupRef.current) {
            const observer = new MutationObserver(() => {
                // Use setTimeout to ensure DOM is fully updated
                setTimeout(removeRbtCloseButtons, 0);
            });
            observer.observe(formGroupRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'style']
            });

            return () => {
                observer.disconnect();
                clearInterval(intervalId);
            };
        }

        return () => clearInterval(intervalId);
    }, []);

    return (
        <FormGroup
            ref={formGroupRef}
            style={props.style}
            controlId={props.controlId}
            onBlur={props.onBlur}
            onKeyDown={props.onKeyDown}
            className="form-text-input-group"
        >
            {props.label &&
                <FormLabel className={"formTextLabel"} isPreview={isPreview} infoText={infoText}>
                    {props.label}
                    {props.required && (
                        <FormRequiredText> *</FormRequiredText>
                    )}
                </FormLabel>
            }

            <ClearRelative>
                <FormControl
                    id={props.id}
                    name={props.name}
                    value={props.value}
                    onChange={(e) => props.onChange(e.target.value)}
                    disabled={props.disabled}
                    placeholder={props.placeholder}
                    type={"text"}
                    isInvalid={props.isInvalid}
                    onBlur={(e) => validationHandler(e, props)}
                    size={props.size}
                    style={{
                        ...props.inputStyle,
                        // Hide browser's native clear button
                        height: height ? height : undefined,
                        borderRadius: 0,
                        WebkitBorderRadius: 0,
                        MozBorderRadius: 0,

                        WebkitAppearance: 'none',
                        appearance: 'none',
                        MozAppearance: 'none',
                        paddingRight: clearable && clearableHandle && (!props.disabled) ? '35px' : undefined,
                    }}
                    className={props.inputClassName}
                />
                {clearable && clearableHandle && (!props.disabled) && (
                    <ClearAbsolute onClick={() => clearableHandle()}>
                        <FontAwesomeIcon
                            color={APP_COLORS['gray-500']}
                            icon={faTimes}
                        />
                    </ClearAbsolute>
                )}
            </ClearRelative>
            {props.validation && props.validation.state && (
                <FormControl.Feedback type='invalid'>
                    {props.validation.message || 'Invalid'}
                </FormControl.Feedback>
            )}
            {props.disabled && props.lockIcon && (
                <DivLockRelative>
                    <DivLockAbsolute>
                        <FontAwesomeIcon
                            color={APP_COLORS['gray-600']}
                            icon={faLock}
                            style={{marginRight: '10px', fontSize: 15}}
                        />
                    </DivLockAbsolute>
                </DivLockRelative>
            )}
            {props.errorMsg && (
                <span className="text-danger" style={{fontSize: '14px'}}>
          {props.errorMsg === true
              ? `Please enter ${props.title}.`
              : props.errorMsg}
        </span>
            )}
        </FormGroup>
    );
};


FormTextInput.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    style: PropTypes.object,
    className: PropTypes.string,
    label: PropTypes.string,
    labelStyle: PropTypes.object,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(['sm', 'lg']),
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    isInvalid: PropTypes.bool,
    validation: PropTypes.shape({
        state: PropTypes.oneOf(['error', 'warning']),
        message: PropTypes.string,
    }),
    lockIcon: PropTypes.bool,
    clearable: PropTypes.bool,
    clearableHandle: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
    onValidateFunc: PropTypes.func,
    reqType: PropTypes.string,
    errorMsg: PropTypes.any,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inputClassName: PropTypes.string,
    inputStyle: PropTypes.object,
};

FormTextInput.defaultProps = {
    style: {},
    title: '',
    name: '',
    className: '',
    labelStyle: {},
    onChange: e => e,
    onBlur: e => e,
    onKeyDown: e => e,
    isInvalid: false,
    validation: {},
    value: '',
    size: 'sm',
    lockIcon: false,
    clearable: false,
    clearableHandle: e => e,
    onValidateFunc: () => {
    },
    reqType: '',
    height: null,
    inputStyle: {},
};

export default FormTextInput;
